// mediaTypes.browser.ts
// Browser-friendly: no Node.js dependencies, pure ESM

/** ---- Types ---- */
export type MimeByExt = Record<string, string>;
export type MediaMap = Record<string, MimeByExt>;
export type MediaCategory = keyof typeof MIME_TYPES;

export interface MediaOptions {
  media_types?: Iterable<string> | string[] | null;
}

/** ---- Data ---- */
export const MIME_TYPES = {
  image: {
    ".jpg": "image/jpeg",
    ".jpeg": "image/jpeg",
    ".png": "image/png",
    ".gif": "image/gif",
    ".bmp": "image/bmp",
    ".tiff": "image/tiff",
    ".webp": "image/webp",
    ".svg": "image/svg+xml",
    ".ico": "image/vnd.microsoft.icon",
    ".heic": "image/heic",
    ".psd": "image/vnd.adobe.photoshop",
    ".raw": "image/x-raw",
  },
  video: {
    ".mp4": "video/mp4",
    ".webm": "video/webm",
    ".ogg": "video/ogg",
    ".mov": "video/quicktime",
    ".avi": "video/x-msvideo",
    ".mkv": "video/x-matroska",
    ".flv": "video/x-flv",
    ".wmv": "video/x-ms-wmv",
    ".3gp": "video/3gpp",
    ".ts": "video/mp2t",
    ".mpeg": "video/mpeg",
    ".mpg": "video/mpg",
  },
  audio: {
    ".mp3": "audio/mpeg",
    ".wav": "audio/wav",
    ".flac": "audio/flac",
    ".aac": "audio/aac",
    ".ogg": "audio/ogg",
    ".m4a": "audio/mp4",
    ".opus": "audio/opus",
  },
  document: {
    ".pdf": "application/pdf",
    ".doc": "application/msword",
    ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".odt": "application/vnd.oasis.opendocument.text",
    ".txt": "text/plain",
    ".rtf": "application/rtf",
    ".md": "text/markdown",
    ".markdown": "text/markdown",
    ".tex": "application/x-tex",
    ".log": "text/plain",
    ".json": "application/json",
    ".xml": "application/xml",
    ".yaml": "application/x-yaml",
    ".yml": "application/x-yaml",
    ".ini": "text/plain",
    ".cfg": "text/plain",
    ".toml": "application/toml",
    ".csv": "text/csv",
    ".tsv": "text/tab-separated-values",
  },
  presentation: {
    ".ppt": "application/vnd.ms-powerpoint",
    ".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ".odp": "application/vnd.oasis.opendocument.presentation",
  },
  spreadsheet: {
    ".xls": "application/vnd.ms-excel",
    ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ".ods": "application/vnd.oasis.opendocument.spreadsheet",
    ".csv": "text/csv",
    ".tsv": "text/tab-separated-values",
  },
  code: {
    ".py": "text/x-python",
    ".java": "text/x-java-source",
    ".c": "text/x-c",
    ".cpp": "text/x-c++",
    ".h": "text/x-c",
    ".hpp": "text/x-c++",
    ".js": "application/javascript",
    ".cjs": "application/javascript",
    ".mjs": "application/javascript",
    ".jsx": "application/javascript",
    ".ts": "application/typescript",
    ".tsx": "application/typescript",
    ".rb": "text/x-ruby",
    ".php": "application/x-php",
    ".go": "text/x-go",
    ".rs": "text/rust",
    ".swift": "text/x-swift",
    ".kt": "text/x-kotlin",
    ".sh": "application/x-shellscript",
    ".bash": "application/x-shellscript",
    ".ps1": "application/x-powershell",
    ".sql": "application/sql",
    ".yml": "application/x-yaml",
    ".coffee": "text/coffeescript",
    ".lua": "text/x-lua",
  },
  archive: {
    ".zip": "application/zip",
    ".tar": "application/x-tar",
    ".gz": "application/gzip",
    ".tgz": "application/gzip",
    ".bz2": "application/x-bzip2",
    ".xz": "application/x-xz",
    ".rar": "application/vnd.rar",
    ".7z": "application/x-7z-compressed",
    ".iso": "application/x-iso9660-image",
    ".dmg": "application/x-apple-diskimage",
    ".jar": "application/java-archive",
    ".war": "application/java-archive",
    ".whl": "application/python-wheel",
    ".egg": "application/python-egg",
  },
  font: {
    ".ttf": "font/ttf",
    ".otf": "font/otf",
    ".woff": "font/woff",
    ".woff2": "font/woff2",
    ".eot": "application/vnd.ms-fontobject",
  },
  executable: {
    ".exe": "application/vnd.microsoft.portable-executable",
    ".dll": "application/vnd.microsoft.portable-executable",
    ".bin": "application/octet-stream",
    ".deb": "application/vnd.debian.binary-package",
    ".rpm": "application/x-rpm",
  },
} as const;

/** category -> Set of extensions */
export const MEDIA_TYPES: Record<MediaCategory, Set<string>> = Object.fromEntries(
  Object.entries(MIME_TYPES).map(([cat, mapping]) => [cat, new Set(Object.keys(mapping))])
) as Record<MediaCategory, Set<string>>;

/** ---- Helpers ---- */
function toCategorySet(categories?: Iterable<string> | string[] | null): Set<MediaCategory> {
  const allCats = new Set(Object.keys(MIME_TYPES)) as Set<string>;
  if (!categories) return new Set(allCats) as Set<MediaCategory>;

  const out = new Set<MediaCategory>();
  for (const c of categories as Iterable<string>) {
    const key = String(c) as MediaCategory;
    if (allCats.has(key)) out.add(key);
  }
  return out.size ? out : (new Set(allCats) as Set<MediaCategory>);
}

function normalizeCategories<T extends MediaOptions | undefined>(
  categories?: Iterable<string> | string[] | null,
  opts?: T
): Set<MediaCategory> {
  return toCategorySet(categories ?? opts?.media_types ?? null);
}

/** pathlib.Path(...).suffix.lower() equivalent—no node:path needed */
function extOf(input: string): string {
  if (!input) return "";
  // Handle bare extensions like ".jpg"
  if (input.startsWith(".") && !input.includes("/") && !input.includes("\\")) {
    const secondary = input.slice(1).lastIndexOf(".");
    if (secondary === -1) return input.toLowerCase();
    return input.slice(secondary + 1).toLowerCase();
  }
  // Strip query/hash for URLs
  const clean = input.split(/[?#]/)[0];
  const lastSlash = Math.max(clean.lastIndexOf("/"), clean.lastIndexOf("\\"));
  const basename = lastSlash >= 0 ? clean.slice(lastSlash + 1) : clean;
  const dotIdx = basename.lastIndexOf(".");
  return dotIdx > 0 ? basename.slice(dotIdx).toLowerCase() : "";
}

function unionExts(categories: Set<MediaCategory>): Set<string> {
  const out = new Set<string>();
  for (const c of categories) {
    for (const e of MEDIA_TYPES[c]) out.add(e);
  }
  return out;
}

/** ---- API ---- */

export function getMediaMap(
  categories?: Iterable<string> | string[] | null,
  opts?: MediaOptions
): Partial<Record<MediaCategory, Set<string>>> {
  const cats = normalizeCategories(categories, opts);
  const result: Partial<Record<MediaCategory, Set<string>>> = {};
  for (const c of cats) result[c] = new Set(MEDIA_TYPES[c]);
  return result;
}

export function getMediaExts(
  categories?: Iterable<string> | string[] | null,
  opts?: MediaOptions
): string[] {
  return Array.from(unionExts(normalizeCategories(categories, opts))).sort();
}

export function confirmType(
  pathOrExt: string,
  categories?: Iterable<string> | string[] | null,
  opts?: MediaOptions
): MediaCategory | null {
  const cats = normalizeCategories(categories, opts);
  const ext = extOf(pathOrExt);
  for (const [category, exts] of Object.entries(MEDIA_TYPES) as [MediaCategory, Set<string>][]) {
    if (cats.has(category) && ext && exts.has(ext)) return category;
  }
  return null;
}

export function isMediaType(
  pathOrExt: string,
  categories?: Iterable<string> | string[] | null,
  opts?: MediaOptions
): boolean {
  return confirmType(pathOrExt, categories, opts) !== null;
}

export function getMimeType(pathOrExt: string): string {
  const ext = extOf(pathOrExt);
  for (const mapping of Object.values(MIME_TYPES)) {
    if (ext && ext in mapping) return mapping[ext as keyof typeof mapping];
  }
  return "application/octet-stream";
}

/** snake_case aliases */
export const get_media_map = getMediaMap;
export const get_media_exts = getMediaExts;
export const confirm_type = confirmType;
export const is_media_type = isMediaType;
export const get_mime_type = getMimeType;